import type { SelectOption } from "components/v2/Select/types";
import type { IntlShape } from "react-intl";

import { capitalizeFirstLetter } from "helpers/commonHelpers";
import type {
  CommodityProjectionRow,
  GoodCustomsInformation,
  HandlingUnitHashMap,
  IndexedPackingListRow,
  PackingListRow,
} from "@RequestFormV2/sections/PackingList/types";
import { isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";

export function isCommodityParentAssigned(commodity: GoodCustomsInformation, parentRow: PackingListRow) {
  if (commodity.parent_assigned === false) return false;
  if (commodity.parent_assigned === true) return true;
  return !isPackingListRowEmpty(parentRow);
}

export function getCommodityAssignedParentIndex(packingListIndex: number, commodity: GoodCustomsInformation) {
  return commodity.parent_packing_list_index ?? packingListIndex;
}

export function getCommodityParentPickerValue(
  packingListIndex: number,
  commodity: GoodCustomsInformation,
  parentRow: PackingListRow,
) {
  if (!isCommodityParentAssigned(commodity, parentRow)) return null;
  return String(getCommodityAssignedParentIndex(packingListIndex, commodity));
}

export function projectCommodityRows(
  packingList: PackingListRow[] | undefined,
  fields?: { id: string }[],
): CommodityProjectionRow[] {
  const rows = packingList ?? [];

  return rows.flatMap((row, packingListIndex) => {
    const commodities = row.good_customs_information ?? [];
    return commodities.map((commodity, commodityIndex) => {
      const packingListFieldId = fields?.[packingListIndex]?.id ?? `packing-list-row-${packingListIndex}`;

      return {
        packingListIndex,
        packingListFieldId,
        commodityIndex,
        commodityKey: commodity.commodity_key ?? `${packingListFieldId}-${commodityIndex}`,
        parentRow: row,
        commodity,
      };
    });
  });
}

interface CommodityParentLabelContext {
  handlingUnitHashMap: HandlingUnitHashMap;
  containerTypeOptions: SelectOption[];
  intl: IntlShape;
}

export function formatCommodityParentPickerLabel(
  row: PackingListRow,
  rowNumber: number,
  { handlingUnitHashMap, containerTypeOptions, intl }: CommodityParentLabelContext,
) {
  let label: string;

  if (isContainerRow(row)) {
    const containerLabel = capitalizeFirstLetter(handlingUnitHashMap.container?.label ?? "Container");
    const containerTypeLabel = containerTypeOptions.find((option) => option.value === row.container_type)?.label;
    label = containerTypeLabel
      ? `${containerLabel} ${capitalizeFirstLetter(containerTypeLabel)}`
      : containerLabel;
  } else if (row.handling_unit) {
    const handlingUnitLabel = handlingUnitHashMap[row.handling_unit.toLowerCase()]?.label;
    label = handlingUnitLabel
      ? capitalizeFirstLetter(handlingUnitLabel)
      : intl.formatMessage(
          {
            id: "request-form.packing-list.handlingUnitRow",
            defaultMessage: "Handling unit {number}",
          },
          { number: rowNumber },
        );
  } else {
    label = intl.formatMessage(
      {
        id: "request-form.packing-list.handlingUnitRow",
        defaultMessage: "Handling unit {number}",
      },
      { number: rowNumber },
    );
  }

  return intl.formatMessage(
    {
      id: "request-form.packing-list.commodityParentRow",
      defaultMessage: "(Row {rowNumber}) {label}",
    },
    { rowNumber, label },
  );
}

interface BuildCommodityParentPickerOptionsParams {
  containerRows: IndexedPackingListRow[];
  handlingUnitRows: IndexedPackingListRow[];
  handlingUnitHashMap: HandlingUnitHashMap;
  containerTypeOptions: SelectOption[];
  intl: IntlShape;
}

export function buildCommodityParentPickerOptions({
  containerRows,
  handlingUnitRows,
  handlingUnitHashMap,
  containerTypeOptions,
  intl,
}: BuildCommodityParentPickerOptionsParams): SelectOption[] {
  const labelContext: CommodityParentLabelContext = { handlingUnitHashMap, containerTypeOptions, intl };

  const containerOptions = containerRows
    .filter(({ row }) => !isPackingListRowEmpty(row))
    .map(({ index, row }, displayIndex) => ({
      value: String(index),
      label: formatCommodityParentPickerLabel(row, displayIndex + 1, labelContext),
    }));

  const handlingUnitOptions = handlingUnitRows
    .filter(({ row }) => !isPackingListRowEmpty(row))
    .map(({ index, row }, displayIndex) => ({
      value: String(index),
      label: formatCommodityParentPickerLabel(row, displayIndex + 1, labelContext),
    }));

  return [...containerOptions, ...handlingUnitOptions];
}
